import { TableCell, TableRow } from '@material-ui/core'
import { ChangeEvent, EventHandler, useCallback, useMemo, useState, KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { AddToAutoRejectionListModal } from '../addToAutoRejectionListModal/AddToAutoRejectionListModal'
import { RemoveFromAutoRejectionListModal } from '../removeFromAutoRejectionListModal/RemoveFromAutoRejectionListModal'
import { CsvUploadModal } from '../csvUploadModal/CsvUploadModal'

import { Table } from '@percent/admin-dashboard/common/components'
import { Badge, Button, ButtonText, SearchInput, Spacer } from '@percent/lemonade'
import { usePermissions } from '@percent/admin-dashboard/common/hooks'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { AutoRejectionListTableProps } from './AutoRejectionListTable.types'
import { AutoRejectionType } from '@percent/admin-dashboard/api/actions/autoRejectionList/autoRejectionList.types'

export function AutoRejectionListTable({
  data,
  isLoading,
  nextPage,
  previousPage,
  totalResults,
  errorMessage,
  queryParams,
  setQueryParams,
  refresh
}: Readonly<AutoRejectionListTableProps>) {
  const { t } = useTranslation()
  const { userHasPermissions } = usePermissions()
  const internalFgaOrgvAgentVerificationsAutoRejectionListFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_AGENT_VERIFICATIONS_AUTO_REJECTION_LIST
  )

  const canUpdate = internalFgaOrgvAgentVerificationsAutoRejectionListFeatureFlag
    ? userHasPermissions('agent_verifications_auto_rejection_list:can_update')
    : true
  const canDelete = internalFgaOrgvAgentVerificationsAutoRejectionListFeatureFlag
    ? userHasPermissions('agent_verifications_auto_rejection_list:can_delete')
    : true

  const [isAddToRejectionListModalOpen, setIsAddToRejectionListModalOpen] = useState(false)
  const [isCsvUploadModalOpen, setIsCsvUploadModalOpen] = useState(false)
  const [selectedAutoRejectionEntry, setSelectedAutoRejectionEntry] = useState<AutoRejectionType>()
  const [searchValue, setSearchValue] = useState<string>(queryParams.query || '')

  const cells = [
    { id: 'email', isSortable: false },
    { id: 'code', isSortable: false },
    ...(canDelete ? [{ id: 'actions', isSortable: false }] : [])
  ]

  const handleKeyChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(
    event => {
      const searchTerm = (event.target as HTMLInputElement).value

      return event.key === 'Enter'
        ? searchTerm.trim() === ''
          ? setQueryParams({ query: undefined })
          : setQueryParams({
              query: searchTerm
            })
        : null
    },
    [setQueryParams]
  )

  const handleClearValue = () => {
    setSearchValue('')
    setQueryParams({ query: undefined })
  }

  const openAddToAutoRejectionListModal = () => setIsAddToRejectionListModalOpen(true)
  const openCsvUploadModal = () => setIsCsvUploadModalOpen(true)

  const filters = useMemo(
    () => (
      <>
        <SearchInput
          value={searchValue ?? ''}
          handleClearValue={handleClearValue}
          onKeyDown={handleKeyPress}
          onChange={handleKeyChange}
          placeholder={t('table.autoRejectionListSearchPlaceholder')}
        />
        <Spacer size={4} axis="horizontal" />
        {canUpdate && (
          <>
            <Button onClick={openAddToAutoRejectionListModal}>+ {t('button.addToAutoRejectionList')}</Button>
            <Spacer size={4} axis="horizontal" />
            <Button onClick={openCsvUploadModal}>+ {t('button.csvUpload')}</Button>
          </>
        )}
      </>
    ),
    [t, handleKeyPress, searchValue, openCsvUploadModal, canUpdate]
  )

  return (
    <>
      <Table
        isLoading={isLoading}
        totalResults={errorMessage ? 0 : totalResults || 0}
        data={data}
        previousPage={previousPage}
        nextPage={nextPage}
        columns={cells}
        filtersContent={filters}
        emptyTableText={t('table.emptyAutoRejectionList')}
      >
        {data?.map(row => (
          <TableRow key={row.id}>
            <TableCell>{row.email}</TableCell>
            <TableCell>
              <Badge variant="default">{t(`dropdown.reject.${row.rejectionReasonCode}`)}</Badge>
            </TableCell>
            {canDelete && (
              <TableCell>
                <ButtonText icon="reject" onClick={() => setSelectedAutoRejectionEntry(row)}>
                  {t('button.remove')}
                </ButtonText>
              </TableCell>
            )}
          </TableRow>
        ))}
      </Table>
      <AddToAutoRejectionListModal
        isOpen={isAddToRejectionListModalOpen}
        setIsOpen={setIsAddToRejectionListModalOpen}
        refresh={refresh}
      />
      {isCsvUploadModalOpen && (
        <CsvUploadModal isOpen={isCsvUploadModalOpen} setIsOpen={setIsCsvUploadModalOpen} refresh={refresh} />
      )}
      <RemoveFromAutoRejectionListModal
        autoRejectionEntry={selectedAutoRejectionEntry}
        setAutoRejectionEntry={setSelectedAutoRejectionEntry}
        refresh={refresh}
      />
    </>
  )
}
